import React from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import clsx from 'clsx';
import Title from './Title';
import Link from './Link';
import detailsImage from '../../../images/features/ana/details.png';
import detailsImageSP from '../../../images/features/ana/details_sp.png';
import exampleImage from '../../../images/features/ana/example.png';
import exampleImageSP from '../../../images/features/ana/example_image_sp.png';
import { anaBackgroundColor, DYNAMIC_PACKAGE } from './Ana';
import { highlightColor } from './Ana';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { styles as containerStyles } from '../styles/container';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(6, 0, 0),
    backgroundColor: anaBackgroundColor,
    width: '100%',
    marginTop: -56,
    paddingTop: 56,
    [theme.breakpoints.up('sm')]: {
      marginTop: -112,
      paddingTop: 112,
      padding: theme.spacing(8, 0, 0),
    },
  },
  titleWrapper: {
    padding: theme.spacing(0, 2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(0, 10),
    },
  },
  content: {
    maxWidth: theme.breakpoints.values.lg,
    width: '100%',
    margin: 'auto',
    padding: '25px 16px',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(6),
    },
    [theme.breakpoints.up('md')]: {
      '& > *:first-child': {
        paddingRight: theme.spacing(3),
      },
      '& > *:nth-child(2)': {
        paddingLeft: theme.spacing(3),
      },
    },
  },
  detailsImage: {
    padding: 28,
    backgroundColor: 'white',

    width: '100%',
    height: 'auto',
  },
  descriptions: {
    marginTop: theme.spacing(2.5),
    '& > p': {
      lineHeight: 1.7,
    },
    '& > p:not(:first-child)': {
      marginTop: theme.spacing(2.5),
      [theme.breakpoints.up('lg')]: {
        marginTop: theme.spacing(5),
      },
    },
  },
  highlight: {
    background: `linear-gradient(transparent 75%, ${highlightColor} 60%)`,
    paddingBottom: '0.3em',
    [theme.breakpoints.up('sm')]: {
      background: `linear-gradient(transparent 60%, ${highlightColor} 60%)`,
    },
  },
  additionalText: {
    fontSize: 14,
  },
  schemaWrapper: {
    ...containerStyles(theme),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(4, 2, 0),
    },
    width: '100%',
    maxWidth: theme.breakpoints.values.lg,
    margin: 'auto',
  },
  containerScheme: {
    width: '100%',
    backgroundColor: theme.props.backgroundColor.primary.main,
    textAlign: 'center',

    padding: '30px 16px',
    [theme.breakpoints.up('md')]: {
      padding: '40px 70px',
    },
  },
  titleScheme: {
    fontWeight: 'bold',
    color: '#333333',

    fontSize: 24,
    marginBottom: 20,
    [theme.breakpoints.up('md')]: {
      fontSize: 20,
      marginBottom: 8,
    },
    '& span': {
      display: 'block',
      [theme.breakpoints.up('md')]: {
        display: 'inline',
      },
    },
  },
  exampleImage: {
    width: '100%',

    maxWidth: 600,
    [theme.breakpoints.up('md')]: {
      maxWidth: 1000,
    },
  },
  additionalScheme: {
    marginTop: 30,
    '&:nth-of-type(n + 2)': {
      marginTop: 15,
    },
  },
}));

const Details = () => {
  const classes = useStyles();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('md'));
  return (
    <div className={classes.root}>
      <div className={classes.titleWrapper}>
        <Title
          texts={['リーズナブルに旅をして', '知らない地域の魅力を再発見']}
        />
      </div>
      <Grid
        container
        alignItems="flex-start"
        spacing={0}
        className={classes.content}
      >
        <Grid item xs={12} md={7}>
          <img
            src={matches ? detailsImage : detailsImageSP}
            alt="details_image"
            className={classes.detailsImage}
          />
        </Grid>
        <Grid item xs={12} md={5} className={classes.descriptions}>
          <Typography>
            <span>ANAトラベラーズとコラボしたことにより、</span>
            <span className={classes.highlight}>以前より快適でおトク</span>
            <span>
              におてつたびに行けるようになりました！こちらのページに掲載されているおてつたびに申し込みされると、
            </span>
            <span className={classes.highlight}>
              おてつたびの日程に合わせたANAトラベラーズの航空券と宿泊を購入
            </span>
            <span>
              して、おてつたび先の地域に訪れることができます。
              前後の日程では、訪れた地域で観光などもご自由に行っていただけます。
            </span>
          </Typography>
          <Typography>
            この機会に今まで行ったことのない地域へ出かけて、おてつたびや前後の観光を通じて地域の魅力を再発見してみよう！
          </Typography>
          <Typography className={classes.additionalText}>
            <span>
              ※宿泊に関しては、おてつたび前後の宿泊先をANAトラベラーズの
            </span>
            <Link href={DYNAMIC_PACKAGE} target="_blank">
              ダイナミックパッケージ
            </Link>
            <span>
              からご予約ができます。おてつたび中の宿泊はおてつたび先に用意していただけます。
            </span>
          </Typography>
        </Grid>
      </Grid>
      <div className={classes.schemaWrapper}>
        <div className={classes.containerScheme}>
          <h3 className={classes.titleScheme}>
            <span>おてつたびが</span>
            <span>おトクに地域に行ける仕組み</span>
          </h3>
          <img
            src={matches ? exampleImage : exampleImageSP}
            alt="example_image"
            className={classes.exampleImage}
          />
          <Typography
            align="left"
            className={clsx(classes.additionalScheme, classes.additionalText)}
          >
            *1 ご自身で負担していただく金額です。
          </Typography>
          <Typography
            align="left"
            className={clsx(classes.additionalScheme, classes.additionalText)}
          >
            ※選択される航空券&宿泊やおてつたび先によって上記の金額は異なります。
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default Details;
